import { Callout } from '@quintype/em/components/callout';
import { FieldLabel } from '@quintype/em/components/field-label';
import Select from '@quintype/em/components/select';
import { TextArea } from '@quintype/em/components/text-area';
import { TextField } from '@quintype/em/components/text-field';
import get from 'lodash/get';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateHaveUnsavedChanges } from '../../actions/builder-action';
import { updateRowsList } from '../../actions/row-action';
import { IAdConfig } from '../../utils/interfaces';
import { checkValidEncodedCustomCode, encodeCustomCode } from '../../utils/utils';
import styles from './select-wrapper.module.css';

interface IComponentName {
  label?: string;
  value?: string;
}

interface IProps {
  rowId?: number;
  slot?: string;
  layout?: string;
  config?: {
    slotConfig?: object[];
    targetingId?: string;
    adType?: string;
    widget?: string;
    widgetHeightDesktop?: string;
    widgetHeightMobile?: string;
    slot2AdType?: string;
    slot2TargetingId?: string;
    slot2Widget?: string;
    slot2WidgetHeightDesktop?: string;
    slot2WidgetHeightMobile?: string;
  };
  selectAd?: string;
  selectTargetId?: string;
}

const adConfig: IAdConfig = {
  Mrec: ['300', '250'],
  'Vertical-Ad': ['300', '600']
};

const layoutTypesList = [
  { label: 'Story', value: 'story' },
  { label: 'Ad', value: 'ad' },
  { label: 'Widget', value: 'widget' }
];
const adTypesList = [
  { label: 'Mrec', value: 'Mrec' },
  { label: 'VerticalAd', value: 'Vertical-Ad' }
];

const SlotSetting = ({ rowId, config = {}, layout = '' }: IProps) => {
  const {
    targetingId = '',
    widget = '',
    slotConfig = [],
    adType = 'Mrec',
    widgetHeightDesktop = '',
    widgetHeightMobile = '',
    slot2AdType = 'Mrec',
    slot2TargetingId = '',
    slot2Widget = '',
    slot2WidgetHeightDesktop = '',
    slot2WidgetHeightMobile = ''
  } = config;

  const firstSlotConfig = slotConfig[0] as any;
  const slot = firstSlotConfig?.type || 'story';

  const secondSlotConfig = slotConfig[1] as any;
  const slot2 = secondSlotConfig?.type || 'story';

  const dispatch = useDispatch();
  let [selectVal, handleSelectValue] = useState(slot);
  let [selectAd, handleAd] = useState(adType);
  let [selectTargetId, handleTargetId] = useState(targetingId);

  // Slot 2 state for ArrowTwoColFifteenStories
  let [slot2SelectVal, handleSlot2SelectValue] = useState(slot2);
  let [slot2SelectAd, handleSlot2Ad] = useState(slot2AdType);
  let [slot2SelectTargetId, handleSlot2TargetId] = useState(slot2TargetingId);

  const widgetPreviewHeight = layout === 'ArrowThirteenStories' ? '600px' : '300px';

  const SlotWigetComponent = () => {
    return (
      <div className={styles.widgetStyleWrapper} style={{ height: widgetPreviewHeight }}>
        Widget
      </div>
    );
  };

  // tslint:disable:no-shadowed-variable
  const SlotAdComponent = ({ selectAd = '', selectTargetId = '' }: IProps) => {
    const adDimensions = adConfig[selectAd] || {};
    const { pageType } = useSelector(state => get(state, 'builder'));
    return (
      <div>
        <div
          className={styles.adStyleWrapper}
          style={{ width: `${adDimensions[0]}px`, height: `${adDimensions[1]}px` }}
        >
          <p>
            adType: {selectAd}
            <br />
            pageType: {pageType}
            <br />
            targetingId: {selectTargetId}
          </p>
        </div>
      </div>
    );
  };

  const getSlotComponentFor = (slotType: string, slotAd: string, slotTargetId: string) => {
    switch (slotType) {
      case 'ad':
        return <SlotAdComponent selectAd={slotAd} selectTargetId={slotTargetId} />;
      case 'widget':
        return <SlotWigetComponent />;
      default:
        return null;
    }
  };

  const getSlotComponent = () => getSlotComponentFor(selectVal, selectAd, selectTargetId);
  const getSlot2Component = () => getSlotComponentFor(slot2SelectVal, slot2SelectAd, slot2SelectTargetId);

  const buildSlotConfig = (
    firstSlotType: string = selectVal || 'story',
    secondSlotType: string = slot2SelectVal || 'story'
  ) => {
    const config =
      layout === 'ArrowTwoColFifteenStories'
        ? [
            { type: firstSlotType, component: getSlotComponent },
            { type: secondSlotType, component: getSlot2Component }
          ]
        : [{ type: firstSlotType, component: getSlotComponent }];

    return config;
  };

  const initialUpdate = () => {
    handleSelectValue(slot);
    const selectedAdValue = selectVal === 'ad' && { adType: selectAd };
    const selected2AdValue = slot2SelectVal === 'ad' && { slot2AdType: slot2SelectAd };

    const slotConfigValue = buildSlotConfig();
    dispatch(
      updateRowsList({
        rowAction: 'update_settings',
        rowActionPayload: {
          selectedRowId: rowId,
          row: {
            ...selectedAdValue,
            ...selected2AdValue,
            slotConfig: slotConfigValue,
            targetingId,
            widget
          }
        }
      })
    );
    dispatch(updateHaveUnsavedChanges(true));
  };

  useEffect(() => {
    initialUpdate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const adTypeHandler = (option: IComponentName) => {
    selectAd = option.value || selectAd;
    handleAd(selectAd);
    dispatch(updateHaveUnsavedChanges(true));
    const slotConfigValue = buildSlotConfig('ad', slot2SelectVal);
    dispatch(
      updateRowsList({
        rowAction: 'update_settings',
        rowActionPayload: {
          selectedRowId: rowId,
          row: { adType: selectAd, slotConfig: slotConfigValue }
        }
      })
    );
  };

  const slotTypeHandler = (option: IComponentName) => {
    selectVal = option.value;
    const setAdType = option.value === 'ad' ? { adType: 'Mrec' } : {};
    handleSelectValue(selectVal);
    const slotConfigValue = buildSlotConfig(option.value || 'story', slot2SelectVal);
    dispatch(updateHaveUnsavedChanges(true));
    dispatch(
      updateRowsList({
        rowAction: 'update_settings',
        rowActionPayload: {
          selectedRowId: rowId,
          row: { ...setAdType, slotConfig: slotConfigValue }
        }
      })
    );
  };

  const updateTargetId = (key: string, value: string) => {
    selectTargetId = value;
    handleTargetId(selectTargetId);
    const slotConfigValue = buildSlotConfig('ad', slot2SelectVal);
    dispatch(updateHaveUnsavedChanges(true));
    dispatch(
      updateRowsList({
        rowAction: 'update_settings',
        rowActionPayload: {
          selectedRowId: rowId,
          row: { adType: selectAd, slotConfig: slotConfigValue, [key]: value }
        }
      })
    );
  };

  const updateWidget = (key: string, value: string) => {
    dispatch(updateHaveUnsavedChanges(true));
    dispatch(
      updateRowsList({
        rowAction: 'update_settings',
        rowActionPayload: {
          selectedRowId: rowId,
          row: { [key]: value }
        }
      })
    );
  };

  // Slot 2 handlers for ArrowTwoColFifteenStories
  const slot2AdTypeHandler = (option: IComponentName) => {
    slot2SelectAd = option.value || slot2SelectAd;
    handleSlot2Ad(slot2SelectAd);
    dispatch(updateHaveUnsavedChanges(true));
    const slotConfigValue = buildSlotConfig(selectVal, slot2SelectVal);
    dispatch(
      updateRowsList({
        rowAction: 'update_settings',
        rowActionPayload: {
          selectedRowId: rowId,
          row: { slot2AdType: slot2SelectAd, slotConfig: slotConfigValue }
        }
      })
    );
  };

  const slot2TypeHandler = (option: IComponentName) => {
    slot2SelectVal = option.value || 'story';
    handleSlot2SelectValue(slot2SelectVal);
    const slotConfigValue = buildSlotConfig(selectVal, slot2SelectVal);
    dispatch(updateHaveUnsavedChanges(true));
    dispatch(
      updateRowsList({
        rowAction: 'update_settings',
        rowActionPayload: {
          selectedRowId: rowId,
          row: {
            slot2AdType: option.value === 'ad' ? 'Mrec' : slot2AdType,
            slotConfig: slotConfigValue
          }
        }
      })
    );
  };

  const updateSlot2TargetId = (key: string, value: string) => {
    slot2SelectTargetId = value;
    handleSlot2TargetId(slot2SelectTargetId);
    dispatch(updateHaveUnsavedChanges(true));
    const slotConfigValue = buildSlotConfig(selectVal, slot2SelectVal);
    dispatch(
      updateRowsList({
        rowAction: 'update_settings',
        rowActionPayload: {
          selectedRowId: rowId,
          row: { [key]: value, slotConfig: slotConfigValue }
        }
      })
    );
  };

  const updateSlot2Widget = (key: string, value: string) => {
    dispatch(updateHaveUnsavedChanges(true));
    const slotConfigValue = buildSlotConfig(selectVal, slot2SelectVal);
    dispatch(
      updateRowsList({
        rowAction: 'update_settings',
        rowActionPayload: {
          selectedRowId: rowId,
          row: { [key]: value, slotConfig: slotConfigValue }
        }
      })
    );
  };

  const getDefaultAd = () => {
    switch (adType) {
      case 'Vertical-Ad':
        return { label: 'VerticalAd', value: 'Vertical-Ad' };
      case 'Mrec':
        return { label: 'Mrec', value: 'Mrec' };
    }
  };

  const getDefaultValue = () => {
    switch (slot) {
      case 'ad':
        return { label: 'Ad', value: 'ad' };
      case 'widget':
        return { label: 'Widget', value: 'widget' };
      case 'story':
        return { label: 'Story', value: 'story' };
    }
  };

  const getAdTypesList = () => {
    // Temp fix - Supporting both Mrec and Vertical ad for 3 col 12 stories

    // if (layout === 'ArrowThreeColTwelveStories') {
    //   return [{ label: 'VerticalAd', value: 'Vertical-Ad' }];
    // }
    const layoutsWithVerticalAds = [
      'ArrowOneColStoryList',
      'ArrowElevenStories',
      'ArrowThreeColTwelveStories',
      'ArrowThirteenStories'
    ];
    if (!layoutsWithVerticalAds.includes(layout)) {
      return [{ label: 'Mrec', value: 'Mrec' }];
    }
    return adTypesList;
  };

  const getlayoutTypesList = () => {
    if (layout === 'ArrowCollectionFilter') {
      return [
        { label: 'Story', value: 'story' },
        { label: 'Ad', value: 'ad' }
      ];
    }
    return layoutTypesList;
  };

  const getRowTemplate = () => {
    if (layout === 'ArrowTwoColFifteenStories') {
      return (
        <>
          {/* Slot 1 */}
          <div style={{ marginBottom: '24px' }}>
            <FieldLabel label="Slot 1 Content" />
            <Select defaultValue={getDefaultValue()} onChange={slotTypeHandler} options={getlayoutTypesList()} />
            {selectVal === 'ad' || slot === 'ad' ? (
              <div>
                <Select defaultValue={getDefaultAd()} onChange={adTypeHandler} options={getAdTypesList()} />
                <div>
                  <TextField
                    label="Targeting Id"
                    value={targetingId}
                    onChange={(value: string) => updateTargetId('targetingId', value)}
                  />
                </div>
              </div>
            ) : null}
            {selectVal === 'widget' || slot === 'widget' ? (
              <>
                <div className={styles.warningMessage}>
                  <Callout
                    variant="warning"
                    title="Warning"
                    content="The code may contain malicious script or may negatively affect the performance. Be cautious before pasting your code."
                  />
                </div>
                <FieldLabel label="Custom Code" />
                <div className={styles.widgetInputBox}>
                  <TextArea
                    label="widget"
                    value={checkValidEncodedCustomCode(widget)}
                    onChange={(value: string) => updateWidget('widget', encodeCustomCode(value))}
                    hint="Paste any valid HTML code here"
                  />
                </div>
                <TextField
                  label="Mobile Placeholder Height (in pixels)"
                  value={widgetHeightMobile}
                  onChange={value => updateWidget('widgetHeightMobile', value)}
                  placeholder="50"
                />
                <TextField
                  label="Desktop Placeholder Height (in pixels)"
                  value={widgetHeightDesktop}
                  onChange={value => updateWidget('widgetHeightDesktop', value)}
                  placeholder="50"
                />
              </>
            ) : null}
          </div>

          {/* Slot 2 */}
          <hr className={styles.slotSeparator} />
          <div>
            <FieldLabel label="Slot 2 Content" />
            <Select
              defaultValue={{ label: slot2.charAt(0).toUpperCase() + slot2.slice(1), value: slot2 }}
              onChange={slot2TypeHandler}
              options={getlayoutTypesList()}
            />
            {slot2SelectVal === 'ad' || slot2 === 'ad' ? (
              <div>
                <Select
                  defaultValue={{ label: slot2AdType === 'Vertical-Ad' ? 'VerticalAd' : 'Mrec', value: slot2AdType }}
                  onChange={slot2AdTypeHandler}
                  options={getAdTypesList()}
                />
                <div>
                  <TextField
                    label="Targeting Id"
                    value={slot2TargetingId}
                    onChange={(value: string) => updateSlot2TargetId('slot2TargetingId', value)}
                  />
                </div>
              </div>
            ) : null}
            {slot2SelectVal === 'widget' || slot2 === 'widget' ? (
              <>
                <div className={styles.warningMessage}>
                  <Callout
                    variant="warning"
                    title="Warning"
                    content="The code may contain malicious script or may negatively affect the performance. Be cautious before pasting your code."
                  />
                </div>
                <FieldLabel label="Custom Code" />
                <div className={styles.widgetInputBox}>
                  <TextArea
                    label="widget"
                    value={checkValidEncodedCustomCode(slot2Widget)}
                    onChange={(value: string) => updateSlot2Widget('slot2Widget', encodeCustomCode(value))}
                    hint="Paste any valid HTML code here"
                  />
                </div>
                <TextField
                  label="Mobile Placeholder Height (in pixels)"
                  value={slot2WidgetHeightMobile}
                  onChange={value => updateSlot2Widget('slot2WidgetHeightMobile', value)}
                  placeholder="50"
                />
                <TextField
                  label="Desktop Placeholder Height (in pixels)"
                  value={slot2WidgetHeightDesktop}
                  onChange={value => updateSlot2Widget('slot2WidgetHeightDesktop', value)}
                  placeholder="50"
                />
              </>
            ) : null}
          </div>
        </>
      );
    } else {
      return (
        <>
          <FieldLabel label="Slot Content" />
          <Select defaultValue={getDefaultValue()} onChange={slotTypeHandler} options={getlayoutTypesList()} />
          {selectVal === 'ad' || slot === 'ad' ? (
            <div>
              <Select defaultValue={getDefaultAd()} onChange={adTypeHandler} options={getAdTypesList()} />
              <div>
                <TextField
                  label="Targeting Id"
                  value={targetingId}
                  onChange={(value: string) => updateTargetId('targetingId', value)}
                />
              </div>
            </div>
          ) : null}
          {selectVal === 'widget' || slot === 'widget' ? (
            <>
              <div className={styles.warningMessage}>
                <Callout
                  variant="warning"
                  title="Warning"
                  content="The code may contain malicious script or may negatively affect the performance. Be cautious before pasting your code."
                />
              </div>
              <FieldLabel label="Custom Code" />
              <div className={styles.widgetInputBox}>
                <TextArea
                  label="widget"
                  value={checkValidEncodedCustomCode(widget)}
                  onChange={(value: string) => updateWidget('widget', encodeCustomCode(value))}
                  hint="Paste any valid HTML code here"
                />
              </div>
              <TextField
                label="Mobile Placeholder Height (in pixels)"
                value={widgetHeightMobile}
                onChange={value => updateWidget('widgetHeightMobile', value)}
                placeholder="50"
              />
              <TextField
                label="Desktop Placeholder Height (in pixels)"
                value={widgetHeightDesktop}
                onChange={value => updateWidget('widgetHeightDesktop', value)}
                placeholder="50"
              />
            </>
          ) : null}
        </>
      );
    }
  };

  return <>{getRowTemplate()}</>;
};

export default SlotSetting;
